import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HashIcon } from 'lucide-react';
import { MetricCard } from './MetricCard';
const messages = [{
  user: 'Sarah Chen',
  avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=150',
  message: 'Team, great progress on the new feature launch! 🚀',
  time: '11:30 AM'
}, {
  user: 'Michael R.',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150',
  message: 'The user feedback has been really positive so far!',
  time: '11:31 AM'
}, {
  user: 'Aisha Johnson',
  avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=150',
  message: 'Agreed! Though we should address the minor UX issues some users reported',
  time: '11:32 AM'
}, {
  user: 'David Kim',
  avatar: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&q=80&w=150',
  message: "Good point Aisha. I've already created tickets for those items 👍",
  time: '11:33 AM'
}, {
  user: 'Emma Wilson',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150',
  message: 'The collaboration on this project has been amazing! Really proud of our team 💪',
  time: '11:34 AM'
}];
const metrics = [{
  name: 'Burnout Level',
  value: '18',
  change: {
    value: 4,
    direction: 'down'
  },
  label: 'Low',
  index: 0
}, {
  name: 'Stress Index',
  value: '37',
  change: {
    value: 3,
    direction: 'up'
  },
  label: 'Moderate',
  index: 1
}, {
  name: 'Psychological Safety',
  value: '87',
  change: {
    value: 1,
    direction: 'up'
  },
  label: 'High',
  index: 2
}, {
  name: 'Engagement',
  value: '74',
  change: {
    value: 5,
    direction: 'up'
  },
  label: 'High',
  index: 3
}, {
  name: 'Morale Score',
  value: '65',
  change: {
    value: 0,
    direction: 'neutral'
  },
  label: 'Moderate',
  index: 4
}, {
  name: 'Social Connection',
  value: '92',
  change: {
    value: 2,
    direction: 'up'
  },
  label: 'High',
  index: 5
}];
const TypingIndicator = () => <motion.div initial={{
  opacity: 0
}} animate={{
  opacity: 1
}} exit={{
  opacity: 0
}} className="flex items-start" data-id="element-51">
    <div className="w-8 h-8 rounded bg-gray-100 mr-3 flex items-center justify-center" data-id="element-52">
      <span className="text-gray-400 text-xs" data-id="element-53">...</span>
    </div>
    <div className="bg-gray-100 rounded-2xl px-4 py-2 inline-block" data-id="element-54">
      <div className="flex space-x-1" data-id="element-55">
        <motion.div animate={{
        y: [-2, 0, -2]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        repeatType: 'loop'
      }} className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-56" />
        <motion.div animate={{
        y: [-2, 0, -2]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        repeatType: 'loop',
        delay: 0.2
      }} className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-57" />
        <motion.div animate={{
        y: [-2, 0, -2]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        repeatType: 'loop',
        delay: 0.4
      }} className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-58" />
      </div>
    </div>
  </motion.div>;
const LoadingDots = () => <div className="flex items-center space-x-1 ml-1" data-id="element-59">
    {[0, 1, 2].map(i => <motion.div key={i} className="w-1.5 h-1.5 bg-white rounded-full" animate={{
    y: ['0%', '-50%', '0%'],
    scale: [1, 1.2, 1]
  }} transition={{
    duration: 0.6,
    repeat: Infinity,
    ease: 'easeInOut',
    delay: i * 0.2
  }} data-id="element-60" />)}
  </div>;
export const LiveSlackFeed = () => {
  const [visibleMessages, setVisibleMessages] = useState<number>(0);
  const [completedFirstCycle, setCompletedFirstCycle] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [activeMetricIndex, setActiveMetricIndex] = useState<number>(-1);
  const [isTyping, setIsTyping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  // Intersection Observer for scroll detection
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !showAnalysis) {
        setTimeout(() => {
          setCompletedFirstCycle(true);
        }, 5000);
      }
    }, {
      threshold: 0.8
    });
    if (feedRef.current) {
      observer.observe(feedRef.current);
    }
    return () => observer.disconnect();
  }, [showAnalysis]);
  // Handle message cycling and analysis phase
  useEffect(() => {
    if (!completedFirstCycle) {
      const messageInterval = 500;
      const typingDuration = 400;
      const showNextMessage = (index: number) => {
        if (index >= messages.length) {
          setCompletedFirstCycle(true);
          setTimeout(() => {
            setShowAnalysis(true);
            setTimeout(() => {
              setShowMetrics(true);
              startMetricsCycle();
            }, 1500);
          }, 500);
          return;
        }
        setIsTyping(true);
        // Continuously scroll while typing
        const scrollInterval = setInterval(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }
        }, 16); // ~60fps
        setTimeout(() => {
          setIsTyping(false);
          setVisibleMessages(index + 1);
          clearInterval(scrollInterval);
          // Immediate next message
          setTimeout(() => {
            showNextMessage(index + 1);
          }, 200); // Shorter gap between messages
        }, typingDuration);
      };
      showNextMessage(0);
    }
  }, [completedFirstCycle]);
  const startMetricsCycle = () => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex >= metrics.length) {
        clearInterval(interval);
        // Reset everything and start over
        setTimeout(() => {
          setShowMetrics(false);
          setShowAnalysis(false);
          setActiveMetricIndex(-1);
          setVisibleMessages(0);
          setCompletedFirstCycle(false);
        }, 3000); // Show all cards for 3 seconds before resetting
        return;
      }
      setActiveMetricIndex(currentIndex);
      currentIndex++;
    }, 400); // Show each card faster
    return () => clearInterval(interval);
  };
  // Enhanced auto-scroll with smooth behavior
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [visibleMessages, isTyping]);
  return <div className="relative max-w-4xl mx-auto" ref={feedRef} data-id="element-61">
      <div className="bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-62">
        {/* Browser Chrome */}
        <div className="bg-gray-100 px-4 py-3 flex items-center border-b border-gray-200" data-id="element-63">
          <div className="flex space-x-2 mr-4" data-id="element-64">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-65"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-66"></div>
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-67"></div>
          </div>
          <div className="flex-1 mx-4" data-id="element-68">
            <div className="bg-white rounded-md py-1 px-3 text-gray-600 text-sm text-center border border-gray-200" data-id="element-69">
              teams.contextuality.ai
            </div>
          </div>
        </div>
        <div className="relative" data-id="element-70">
          {/* Analysis Banner */}
          {showAnalysis && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4
        }} className="absolute top-0 left-0 right-0 z-20 bg-[#8679DF] text-white h-[32px] flex items-center justify-center shadow-sm" data-id="element-71">
              <span data-id="element-72">Analyzing</span>
              <LoadingDots data-id="element-73" />
            </motion.div>}
          <div className="bg-white" data-id="element-74">
            {/* Channel Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center" data-id="element-75">
              <HashIcon size={18} className="text-gray-500 mr-2" data-id="element-76" />
              <span className="text-gray-800 font-medium" data-id="element-77">team-general</span>
              <span className="ml-2 text-gray-500 text-sm" data-id="element-78">12 members</span>
            </div>
            {/* Messages Container */}
            <div ref={containerRef} style={{
            filter: showAnalysis ? 'blur(2px) saturate(60%)' : 'none',
            opacity: showAnalysis ? '0.4' : '1'
          }} className="h-[400px] overflow-y-auto p-4 space-y-4 transition-all duration-700 scroll-smooth" data-id="element-79">
              <AnimatePresence mode="sync" data-id="element-80">
                {messages.slice(0, visibleMessages).map((msg, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                ease: [0.23, 1, 0.32, 1]
              }} className="flex items-start" data-id="element-81">
                    <img src={msg.avatar} alt={msg.user} className="w-8 h-8 rounded mr-3" data-id="element-82" />
                    <div data-id="element-83">
                      <div className="flex items-baseline" data-id="element-84">
                        <span className="font-bold text-gray-900 mr-2" data-id="element-85">
                          {msg.user}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-86">
                          {msg.time}
                        </span>
                      </div>
                      <p className="text-gray-800 mt-1" data-id="element-87">{msg.message}</p>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
              {/* Typing Indicator */}
              <AnimatePresence mode="sync" data-id="element-88">
                {isTyping && !completedFirstCycle && <TypingIndicator data-id="element-89" />}
              </AnimatePresence>
            </div>
            {/* New Metrics Grid */}
            {showMetrics && <div className="absolute inset-0 flex items-center justify-center" data-id="element-90">
                <div className="grid grid-cols-2 gap-4 p-6 w-full max-w-[900px]" style={{
              gridTemplateAreas: `
                      "burnout stress"
                      "safety engagement"
                      "morale social"
                    `
            }} data-id="element-91">
                  {metrics.map((metric, index) => <div key={index} style={{
                gridArea: metric.name === 'Burnout Level' ? 'burnout' : metric.name === 'Stress Index' ? 'stress' : metric.name === 'Psychological Safety' ? 'safety' : metric.name === 'Engagement' ? 'engagement' : metric.name === 'Morale Score' ? 'morale' : 'social'
              }} data-id="element-92">
                      <MetricCard {...metric} isVisible={index <= activeMetricIndex} data-id="element-93" />
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};